import { z } from 'zod';
import { GENDERS, MIN_PROVIDER_AGE } from './roles';

/**
 * Normalises Ethiopian mobile numbers to +2519XXXXXXXX / +2517XXXXXXXX.
 * Accepts 09.., 07.., 9.., 2519.., +251 9.. with spaces or dashes. Returns null if invalid.
 */
export function normalizeEthiopianPhone(input: string): string | null {
  const digits = input.replace(/[\s\-().]/g, '').replace(/^\+/, '');
  let local: string | null = null;
  if (/^251[79]\d{8}$/.test(digits)) local = digits.slice(3);
  else if (/^0[79]\d{8}$/.test(digits)) local = digits.slice(1);
  else if (/^[79]\d{8}$/.test(digits)) local = digits;
  return local ? `+251${local}` : null;
}

export const phoneSchema = z
  .string()
  .max(20)
  .transform((v, ctx) => {
    const n = normalizeEthiopianPhone(v);
    if (!n) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'INVALID_PHONE' });
    return n ?? v;
  });

/** GPS coordinates, or a chosen city (and optional area). At least one is required. */
export const locationInputSchema = z
  .object({
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    cityId: z.string().uuid().optional(),
    areaId: z.string().uuid().optional(),
  })
  .refine((l) => (l.lat !== undefined && l.lng !== undefined) || !!l.cityId, { message: 'LOCATION_REQUIRED' });
export type LocationInput = z.infer<typeof locationInputSchema>;

const displayName = z.string().trim().min(2, 'NAME_TOO_SHORT').max(40, 'NAME_TOO_LONG');

export const clientProfileSchema = z.object({
  displayName,
  gender: z.enum(GENDERS),
  phone: phoneSchema,
  location: locationInputSchema,
});
export type ClientProfileInput = z.infer<typeof clientProfileSchema>;

export const clientProfileUpdateSchema = clientProfileSchema.partial();

/** Session prices in whole birr, keyed by session length in minutes ("60", "120"). */
export const pricesSchema = z.record(z.string().regex(/^\d+$/), z.number().int().min(50).max(100_000));

/** Everything the provider wizard saves as they go. All optional until submit. */
export const providerDraftSchema = z.object({
  displayName: displayName.optional(),
  gender: z.enum(GENDERS).optional(),
  phone: z.string().max(20).optional(),
  age: z.number().int().min(0).max(120).optional(),
  prices: pricesSchema.optional(),
  photoKeys: z.array(z.string().max(200)).max(3).optional(),
  location: z
    .object({
      lat: z.number().optional(),
      lng: z.number().optional(),
      cityId: z.string().uuid().optional(),
      areaId: z.string().uuid().optional(),
    })
    .optional(),
  bio: z.string().max(70).optional(),
  idPhotoKey: z.string().max(200).optional(),
  selfieKey: z.string().max(200).optional(),
  agentCode: z.string().max(12).optional(),
});
export type ProviderDraft = z.infer<typeof providerDraftSchema>;

/** Checked on submit: the complete, valid provider application. */
export const providerSubmitSchema = z.object({
  displayName,
  gender: z.enum(GENDERS),
  phone: phoneSchema,
  age: z.number().int().min(MIN_PROVIDER_AGE, 'UNDER_18').max(80),
  prices: pricesSchema.refine((p) => Object.keys(p).length > 0, 'PRICE_REQUIRED'),
  photoKeys: z.array(z.string()).min(1, 'PHOTO_REQUIRED').max(3),
  location: locationInputSchema,
  bio: z.string().trim().max(70).optional(),
  idPhotoKey: z.string().min(1, 'ID_PHOTO_REQUIRED'),
  selfieKey: z.string().min(1, 'SELFIE_REQUIRED'),
  agentCode: z.string().max(12).optional(),
});
export type ProviderSubmit = z.infer<typeof providerSubmitSchema>;

/**
 * What an approved provider can change themselves. Gender and age were checked against
 * their ID, so only an admin can change those.
 */
export const providerEditSchema = providerSubmitSchema
  .pick({ displayName: true, phone: true, prices: true, photoKeys: true, location: true, bio: true })
  .partial();
export type ProviderEdit = z.infer<typeof providerEditSchema>;

export const UPLOAD_PURPOSES = ['provider_photo', 'id_photo', 'id_selfie', 'payment_evidence'] as const;
export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number];

export const denySchema = z.object({ reason: z.string().trim().min(3).max(300) });

export const createAgentInviteSchema = z.object({
  name: z.string().trim().min(2).max(60),
  phone: z.string().max(20).optional(),
});
